
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PollWidgetNavigation from './PollWidgetNavigation';
import { Poll } from '@/types/poll';
import PollDisplay from '@/components/polls/shared/PollDisplay';
import { Link } from 'react-router-dom';
import LikeButton from '@/components/social/LikeButton';
import ShareButton from '@/components/social/ShareButton';
import { MessageSquare } from 'lucide-react';
import { usePollSocialMetrics } from '@/hooks/usePollSocialMetrics';
import { useAuth } from '@/contexts/auth';
import { useCentralizedVoting } from '@/hooks/voting/useCentralizedVoting';
import { toast } from 'sonner';
import { useAnonymousVoting } from '@/components/polls/shared/poll-display/useAnonymousVoting';
import { useState, useEffect } from 'react';

interface ActivePollsSectionProps {
  filteredPolls: Poll[];
  currentPoll: Poll;
  currentPollIndex: number;
  hasVoted: boolean;
  canVote: boolean;
  handleVote: (optionId: number) => void;
  nextPoll: () => void;
  prevPoll: () => void;
  refresh: () => void;
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  userVotes?: Record<string, number>;
  cannotVoteReason?: string;
}

const ActivePollsSection = ({
  filteredPolls,
  currentPoll,
  currentPollIndex,
  hasVoted,
  canVote,
  handleVote,
  nextPoll,
  prevPoll,
  refresh,
  selectedCategory,
  setSelectedCategory,
  userVotes = {},
  cannotVoteReason = "You've already voted on this poll"
}: ActivePollsSectionProps) => {
  // Get social metrics for current poll if it exists - MAINTAIN EXISTING FEATURE
  const { metrics: socialMetrics } = usePollSocialMetrics(currentPoll?.id);
  const { user, isAuthenticated } = useAuth();
  
  // Get anonymous voting context
  const {
    anonymousId,
    hasAnonymousVoted
  } = useAnonymousVoting(currentPoll?.id, isAuthenticated);
  
  // Use centralized voting with enhanced selected option tracking
  const { submitVote: centralizedSubmitVote, getUserSelectedOption } = useCentralizedVoting(anonymousId);
  
  // Track selected option for current poll
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  // Load selected option when poll changes
  useEffect(() => {
    const loadSelectedOption = async () => {
      if (currentPoll?.id) {
        try {
          const selected = await getUserSelectedOption(currentPoll.id);
          setSelectedOption(selected);
        } catch (error) {
          console.error('Error loading selected option:', error);
          setSelectedOption(null);
        }
      }
    };

    loadSelectedOption();
  }, [currentPoll?.id, getUserSelectedOption]);

  if (filteredPolls.length === 0) {
    return (
      <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-600 mb-4">
          No active polls found for the selected category.
        </p>
        <Button onClick={() => setSelectedCategory(null)} variant="outline" className="flex items-center">
          Show All Polls
        </Button>
      </div>
    );
  }

  // Create a copy of the poll with user vote information if available - MAINTAIN EXISTING FEATURE
  const displayPoll = { ...currentPoll };
  
  // Ensure results exist and include the user's vote if they've voted - MAINTAIN EXISTING FEATURE
  const actuallyHasVoted = hasVoted || hasAnonymousVoted || selectedOption !== null;
  if (actuallyHasVoted) {
    const userVoteOptionId = selectedOption || userVotes[currentPoll.id];
    displayPoll.results = {
      ...(displayPoll.results || { totalVotes: displayPoll.total_votes, topOption: '' }),
      selectedOptionId: userVoteOptionId
    };
  }

  // Modified vote handler using centralized voting
  const handlePollVote = async (optionId: number) => {
    try {
      const result = await centralizedSubmitVote(currentPoll, optionId);
      
      if (result.success) {
        // Update local selected option state
        setSelectedOption(optionId);
        
        // Call the original handler to update UI
        handleVote(optionId);
        
        // Refresh after a short delay for logged-in users
        if (user) {
          setTimeout(() => {
            refresh();
          }, 500);
        }
      }
    } catch (error) {
      toast.error('Failed to submit vote. Please try again.');
    }
  };

  const handleRefreshPoll = () => {
    if (refresh) {
      refresh();
    }
  };

  // Use consistent poll routing - always use /poll/:id to avoid conflicts
  const pollUrl = `/poll/${currentPoll.id}`;

  return (
    <>
      <Card className="mb-6 border-0 shadow-none p-6">
        <div className="mb-4">
          <Link to={pollUrl}>
            <h3 className="text-xl font-bold text-nation-blue dark:text-blue-300 hover:underline">
              {currentPoll.title}
            </h3>
          </Link>
        </div>
        
        {/* MAINTAIN ALL EXISTING POLL DISPLAY FEATURES INCLUDING VOTE FILTERING */}
        <PollDisplay
          poll={displayPoll}
          hasVoted={actuallyHasVoted}
          onVote={handlePollVote}
          canVote={canVote}
          cannotVoteReason={cannotVoteReason}
          onRefresh={handleRefreshPoll}
          variant="default"
          showShareButtons={false}
          showLocation={true}
        />

        {/* Social interactions bar - MAINTAIN ALL EXISTING FEATURES */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mt-4 pt-4 border-t">
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <LikeButton 
              cardId={currentPoll.id}
              initialLikes={socialMetrics?.likesCount || 0}
              showCount={true}
              size="sm"
              contentType="poll"
            />
            
            <Link to={`${pollUrl}#comments`}>
              <Button variant="outline" size="sm" className="shrink-0">
                <MessageSquare className="h-4 w-4 mr-1" />
                <span className="hidden xs:inline">{socialMetrics?.commentsCount || 0}</span>
                <span className="xs:hidden">{socialMetrics?.commentsCount || 0}</span>
              </Button>
            </Link>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
            <ShareButton
              contentId={currentPoll.id}
              contentType="poll"
              title={`Poll: ${currentPoll.title}`}
              description={currentPoll.description || ''}
              url={`${window.location.origin}${pollUrl}`}
              initialShares={socialMetrics?.sharesCount || 0}
              showCount={true}
              size="sm"
            />
            
            <Link to={pollUrl}>
              <Button variant="outline" size="sm" className="shrink-0">
                <span className="hidden sm:inline">View Details</span>
                <span className="sm:hidden">Details</span>
              </Button>
            </Link>
          </div>
        </div>
      </Card>
      
      {/* MAINTAIN EXISTING NAVIGATION FEATURE */}
      <PollWidgetNavigation
        currentIndex={currentPollIndex}
        totalPolls={filteredPolls.length}
        onPrevious={prevPoll}
        onNext={nextPoll}
      />
    </>
  );
};

export default ActivePollsSection;
